/* eslint-disable consistent-default-export-name/default-import-match-filename */
import HeaderRow from './HeaderRow'
import RowList from './RowList'
import TableElement from './server/elements/Table'
import classNames from 'classnames'
import styles from './server/elements/Table.module.css'
import useResizeObserver from '../../hooks/useResizeObserver'
import {
  type ForwardedRef,
  type RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Icon } from '../Icon'
import { TableStoreProvider, useExpandable, useIsPivot, usePivotEntities, useResizeContext } from './utils/TableStore'
import type { TableConfig } from './types'

function Header() {
  const pivot = useIsPivot()
  if (pivot) return null
  return <HeaderRow />
}

function ScrollToEndButton({ tableRef, label }: { tableRef: RefObject<HTMLTableElement | null>; label: string }) {
  const [isMaxScroll, setIsMaxScroll] = useState(false)

  useEffect(() => {
    function setMaxScroll(element: HTMLTableElement) {
      const maxScroll = Math.round(element.scrollLeft + element.clientWidth) >= Math.round(element.scrollWidth)
      setIsMaxScroll(maxScroll)
    }
    if (!tableRef.current) return
    const container = tableRef.current.parentElement as HTMLTableElement
    setMaxScroll(container)
    function listener(event: Event) {
      const target = event.target as HTMLTableElement
      setMaxScroll(target)
    }
    container.addEventListener('scroll', listener)
    return () => {
      container.removeEventListener('scroll', listener)
    }
  }, [tableRef])

  const onClick = useCallback(() => {
    if (!tableRef.current) return
    const container = tableRef.current.parentElement as HTMLDivElement
    container.scrollTo({
      left: container.scrollWidth - container.clientWidth,
      behavior: 'smooth',
    })
  }, [tableRef])

  return (
    <button
      onClick={onClick}
      className={classNames(styles.scrollToEndButton, { [styles.hidden!]: isMaxScroll })}
      aria-hidden={isMaxScroll}
      title={label}
    >
      <div className={styles.iconWrapper}>
        <Icon name="chevronRight" color="cta-secondary" size="300" className={styles.icon} />
      </div>
    </button>
  )
}

const TableInner = forwardRef(function TableInner<T extends Record<string, unknown>>(
  { className }: { className?: string },
  ref: ForwardedRef<HTMLTableElement>
) {
  const { resizing, touchEnabled } = useResizeContext()
  const pivot = useIsPivot()
  const expandable = useExpandable()

  return (
    <TableElement
      ref={ref}
      resizing={resizing}
      head={<Header />}
      pivot={pivot}
      expandable={expandable}
      className={className}
      touchEnabled={touchEnabled ?? undefined}
    >
      <RowList />
    </TableElement>
  )
})

function Debug() {
  const { columnWidths, resizing, touchEnabled } = useResizeContext()
  const entities = usePivotEntities()
  return (
    <>
      <div style={{ border: resizing ? '1px solid red' : '1px solid black', marginBlockStart: 50, padding: 5 }}>
        [{touchEnabled ? 'touch' : 'mouse'}]: {JSON.stringify(columnWidths.map((c) => c.width))}
      </div>
      <pre>{JSON.stringify(entities, null, 2)}</pre>
    </>
  )
}
function Table<T extends Record<string, unknown>>({
  columns,
  data,
  getRowId,
  pivot,
  expandable,
  id: id_,
  className,
  pivotLabelColumnConfig,
  debug,
  scrollToEndLabel,
}: TableConfig<T> & { debug?: boolean }) {
  const generatedId = useId()
  const id = id_ ?? generatedId
  const config = useMemo(
    () => ({ columns, getRowId, pivot, expandable, id, pivotLabelColumnConfig }),
    [columns, getRowId, pivot, expandable, id, pivotLabelColumnConfig]
  )
  const tableRef = useRef<HTMLTableElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { width: wrapperWidth } = useResizeObserver({ ref: containerRef })
  const { width: tableWidth } = useResizeObserver({ ref: tableRef })
  const overflows = (wrapperWidth ?? 0) < (tableWidth ?? 0)

  return (
    <TableStoreProvider data={data} config={config} tableRef={tableRef}>
      <div className={styles.wrapper}>
        <TableInner
          ref={(ref) => {
            tableRef.current = ref
            containerRef.current = ref?.parentElement as HTMLDivElement
          }}
          className={className}
        />
        {overflows && <ScrollToEndButton tableRef={tableRef} label={scrollToEndLabel} />}
      </div>
      {debug && <Debug />}
    </TableStoreProvider>
  )
}

export default Table
